import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

const PAGE_SIZE = 50;

const ACTION_STYLES = {
  create: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-900",
  update: "bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/50 dark:text-sky-300 dark:border-sky-900",
  delete: "bg-red-50 text-red-800 border-red-200 dark:bg-red-950/50 dark:text-red-300 dark:border-red-900",
};

const ENTITY_LABELS = {
  revenue_detail: "Revenue Detail",
  engagement: "Engagement",
  service: "Service",
  business_days: "Business Days",
  summary_override: "Summary Override",
  workspace: "Workspace",
  user: "User",
};

export default function AuditTrailPage() {
  const [data, setData] = useState({ items: [], total: 0, entity_types: [] });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [entityFilter, setEntityFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE };
    if (entityFilter !== "all") params.entity_type = entityFilter;
    if (actionFilter !== "all") params.action = actionFilter;
    if (search) params.search = search;
    api.get("/audit", { params })
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, entityFilter, actionFilter, search]);

  useEffect(load, [load]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  const fmtTs = (ts) => {
    try {
      return new Date(ts).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return ts;
    }
  };

  return (
    <div className="space-y-4" data-testid="audit-trail-page">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
          <Input data-testid="audit-search-input" placeholder="Search description or user..."
            className="pl-9 rounded-lg" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[170px] rounded-lg" data-testid="audit-entity-filter">
            <SelectValue placeholder="Entity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entities</SelectItem>
            {data.entity_types.map((e) => (
              <SelectItem key={e} value={e}>{ENTITY_LABELS[e] || e}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[130px] rounded-lg" data-testid="audit-action-filter">
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            <SelectItem value="create">Create</SelectItem>
            <SelectItem value="update">Update</SelectItem>
            <SelectItem value="delete">Delete</SelectItem>
          </SelectContent>
        </Select>
        <p className="ml-auto text-xs text-muted-foreground" data-testid="audit-total">
          {data.total} entries
        </p>
      </div>

      <div className="rounded-xl border bg-card shadow-[var(--app-shadow-sm)] overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-2">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : data.items.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground" data-testid="audit-empty">
            No audit entries yet. Changes to data in this workspace will appear here.
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-18rem)]">
            <table className="sticky-table w-full text-xs" data-testid="audit-table">
              <thead>
                <tr className="border-b">
                  {["Timestamp", "User", "Action", "Entity", "Description", "Changes"].map((h) => (
                    <th key={h} className="px-3 py-2.5 font-semibold text-muted-foreground text-left whitespace-nowrap border-b">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => {
                  const changeCount = Object.keys(item.changes || {}).length;
                  return (
                    <tr key={item.id} className="border-b last:border-0 hover:bg-muted/40 transition-[background-color] duration-150" data-testid={`audit-row-${item.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-muted-foreground tabular-nums">{fmtTs(item.timestamp)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="font-medium text-foreground">{item.user_name || item.user_email}</span>
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={`rounded-full font-medium text-[10px] px-2 border hover:bg-transparent ${ACTION_STYLES[item.action] || "bg-muted text-muted-foreground"}`}>
                          {item.action}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{ENTITY_LABELS[item.entity_type] || item.entity_type}</td>
                      <td className="px-3 py-2 max-w-[480px]">
                        <span className="text-foreground">{item.description}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                        {changeCount > 0 ? (
                          <span title={Object.entries(item.changes).map(([k, v]) => `${k}: ${JSON.stringify(v.from)} -> ${JSON.stringify(v.to)}`).join("\n")}
                            className="underline decoration-dotted cursor-help">
                            {changeCount} field{changeCount > 1 ? "s" : ""}
                          </span>
                        ) : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && data.total > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t px-4 py-2.5 text-xs text-muted-foreground">
            <span>Page {page} of {totalPages}</span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" className="h-7 w-7 rounded-lg p-0" disabled={page <= 1}
                onClick={() => setPage(1)} data-testid="audit-first-page" title="First page">
                <ChevronsLeft className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="h-7 rounded-lg" disabled={page <= 1}
                onClick={() => setPage(page - 1)} data-testid="audit-prev-page">
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </Button>
              <Button variant="outline" size="sm" className="h-7 rounded-lg" disabled={page >= totalPages}
                onClick={() => setPage(page + 1)} data-testid="audit-next-page">
                Next <ChevronRight className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="h-7 w-7 rounded-lg p-0" disabled={page >= totalPages}
                onClick={() => setPage(totalPages)} data-testid="audit-last-page" title="Last page">
                <ChevronsRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
